// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A dialog component for canceling asset scrapping.
 * This module provides an interface for returning a scrapped
 * asset back to service, typically when an asset was scrapped
 * by accident or a lost asset has been found.
 *
 * Key features:
 * - Simple confirmation
 * - Data preservation
 * - Audit logging
 * - Error handling
 * - Cache invalidation
 * - State restoration
 * - Mutation handling
 */
import { Asset } from '@/asset/api/types';
import { unscrapAsset } from '@/asset/api/unscrap-asset';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * Props for configuring the AssetUnscrapDialog component.
 * @typedef {Object} Props
 */
interface Props {
  /**
   * Asset information needed for unscrapping.
   * Only requires minimal identification details
   * since we're just restoring the asset to service.
   *
   * Required properties:
   * - key: Unique identifier
   * - serial: Human-readable identifier
   *
   * @example
   * ```ts
   * const asset = {
   *   key: 'asset-123',
   *   serial: 'SN123456'
   * };
   * ```
   */
  asset: Pick<Asset, 'key' | 'serial'>;

  /**
   * Controls the visibility of the dialog.
   * When true, the dialog is displayed.
   *
   * @default false
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog should close.
   * Called after successful submission or when
   * the user cancels the operation.
   */
  onClose: () => void;
}

/**
 * A dialog component for restoring scrapped assets to service.
 * Provides a confirmation interface for canceling an asset's
 * scrapped status, with clear explanations of the consequences
 * and data preservation details.
 *
 * Features:
 * - Simple confirmation workflow
 * - Clear explanation of changes
 * - Data preservation notice
 * - Error handling with recovery
 * - Cache invalidation on success
 * - Audit log preservation
 *
 * Business rules:
 * - Disposal details are removed from reporting
 * - Original records preserved in audit log
 * - Asset becomes available for assignments
 * - No date input required (immediate effect)
 *
 * Usage example:
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 *
 * return (
 *   <AssetUnscrapDialog
 *     asset={{
 *       key: 'asset-123',
 *       serial: 'SN123456'
 *     }}
 *     isOpen={isOpen}
 *     onClose={() => setIsOpen(false)}
 *   />
 * );
 * ```
 *
 * @param props - Component configuration options
 */
export function AssetUnscrapDialog(props: Props) {
  const queryClient = useQueryClient();

  const { mutate, reset, isError } = useMutation({
    mutationFn: () => {
      return unscrapAsset(props.asset.key);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset'] });
      props.onClose();
    },
  });

  /**
   * Handles the confirmation button click.
   * Triggers the unscrap mutation to restore
   * the asset to service immediately.
   */
  const handleClickSubmit = () => {
    mutate();
  };

  return (
    <>
      <SupportSnackbar
        isOpen={isError}
        onClose={reset}
        message="An error occurred while unscrapping the asset."
      />
      <Dialog open={props.isOpen} onClose={props.onClose}>
        <DialogTitle>Cancel asset scrap</DialogTitle>
        <DialogContent dividers>
          <Typography marginBottom={2}>
            Marking <b>{props.asset.serial}</b> back in service will return it
            to use. This usually applies if an asset was scrapped by accident
            (such as if it was lost then found).
          </Typography>
          <Alert severity="info">
            The asset disposal date, disposal reason, and disposal price will be
            removed from your asset reporting. These records will be preserved
            in the audit log.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleClickSubmit}>
            Cancel asset scrap
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
